import { getRouteApi } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { SelectOption } from '@/components/Select/Select'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { useColumns } from '@/hooks/useColumns'
import { usePagination } from '@/hooks/usePagination'
import useRowSelection from '@/hooks/useRowSelection'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { useSorting } from '@/hooks/useSorting'
import { getFilterValue } from '@/utils/get-filter-value'
import { getValue } from '@/utils/get-value'

import SectionsListFilters from '../components/SectionsListFilters'
import SectionsListLayout from '../components/SectionsListLayout'
import SectionsListTable, {
  getSectionsListColumns
} from '../components/SectionsListTable'
import type { SectionListFiltersKey } from '../constants/section-list-filters'
import { useSections, type SectionsListItem } from '../queries/useSections'
import { useSectionsIds } from '../queries/useSectionsIds'

const routeApi = getRouteApi('/_auth/sections/list')

const SectionsListView = () => {
  const { t } = useTranslation('sections')

  const navigate = routeApi.useNavigate()
  const {
    page: _,
    pageSize: __,
    sort,
    order,
    ...filters
  } = routeApi.useSearch()

  const pagination = usePagination(routeApi)
  const sorting = useSorting(routeApi)

  const params = {
    ...pagination.pagination,
    ids: filters.ids?.map(getValue),
    courseIds: filters.courseIds?.map(getValue),
    curriculumIds: filters.curriculumIds?.map(getValue),
    statuses: filters.statuses,
    sort,
    order
  }

  const handleFilterChange = (
    key: SectionListFiltersKey,
    value?: SelectOption<string>[] | string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const { isExtraLarge } = useScreenResolution()
  const { columns, ...manageColumns } = useColumns(
    getSectionsListColumns(t, isExtraLarge),
    { routeApi }
  )

  const {
    data: sectionsList,
    isLoading: isSectionsListLoading,
    refetch: refetchSectionsList
  } = useSections(params)
  const {
    data: sectionsIdsList,
    isLoading: isSectionsIdsListLoading,
    refetch: refetchSectionsIdsList
  } = useSectionsIds(params)
  const {
    data: sectionsListTotalCount,
    isLoading: isSectionsListTotalCountLoading,
    refetch: refetchSectionsListTotalCount
  } = useSectionsIds()

  const refetchData = () => {
    refetchSectionsList()
    refetchSectionsIdsList()
    refetchSectionsListTotalCount()
  }

  const isLoading =
    isSectionsListLoading ||
    isSectionsIdsListLoading ||
    isSectionsListTotalCountLoading

  const rowSelection = useRowSelection({
    count: sectionsIdsList?.length ?? 0,
    itemsToSelect: (sectionsIdsList ?? []).map(curriculum => curriculum.id)
  })

  const handleRemoveClick = (section: SectionsListItem) => {
    // TODO: Implement logic - https://panowie.atlassian.net/browse/RG-270
    console.log(`Remove: ${section.id}`)
    refetchData()
  }

  const handleBulkRemoveClick = (sectionIds: string[]) => {
    // TODO: Implement logic - https://panowie.atlassian.net/browse/RG-270
    console.log(`Bulk remove sections: ${sectionIds.join(', ')}`)
    refetchData()
  }

  return (
    <SectionsListLayout>
      <SectionsListFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        manageColumns={manageColumns}
      />
      <SectionsListTable
        columns={columns}
        data={sectionsList?.list}
        count={sectionsList?.count}
        totalCount={sectionsListTotalCount?.length}
        isLoading={isLoading}
        onRemoveClick={handleRemoveClick}
        onBulkRemoveClick={handleBulkRemoveClick}
        rowSelection={rowSelection}
        {...pagination}
        {...sorting}
      />
    </SectionsListLayout>
  )
}

export default SectionsListView
